<template>
    <div class="portfolioOverviewPage">
        <PortfoliosOverviewSummary></PortfoliosOverviewSummary>
        <v-data-table
            :headers="headers"
            :items="sortedPortfolioStock"
            item-key="id"
            :sort-by="['statistics.normalizedWithPreferredCurrency.totalMaterializedGrossValueIncludingUnrealized']"
            :sort-desc="[true]"
            :items-per-page=-1
            hide-default-footer
        >
            <template v-slot:item.symbol="{ item }">
                <div class="colEmph">{{item.stockDateTimeSnapshot.symbol}}</div>
                <div class="sub">{{item.statistics.companyName}}</div>
            </template>
            <template v-slot:item.country="{ item }">
                <CountryBadge :currency="item.currency"></CountryBadge>
            </template>
            <template v-slot:item.stockDateTimeSnapshot.price="{ item }">
                <StockPriceInfos :entry="stockPriceInfo(item)"></StockPriceInfos>
            </template>
            <template v-slot:item.stockDateTimeSnapshot.changePercent="{ item }">
                <ValueAndChange v-bind="buildChangeData(item)"></ValueAndChange>
            </template>
            <template v-slot:item.dayLow="{ item }">
                <div>{{ formatPrice(item.stockDateTimeSnapshot.dayLow) }}</div>
            </template>
            <template v-slot:item.dayHigh="{ item }">
                <div>{{ formatPrice(item.stockDateTimeSnapshot.dayHigh) }}</div>
            </template>
            <template v-slot:item.statistics.allTimeAverageShareBuyPrice="{ item }">
                <div>{{ formatPrice(item.statistics.allTimeAverageShareBuyPrice) }}</div>
            </template>
            <template v-slot:item.statistics.normalizedWithPreferredCurrency.totalMaterializedGrossValueIncludingUnrealized="{ item }">
                <div class="colEmph">{{ formatPrice(item.statistics.normalizedWithPreferredCurrency.totalMaterializedGrossValueIncludingUnrealized) }}</div>
            </template>
            <template v-slot:item.statistics.normalizedWithPreferredCurrency.dayChange="{ item }">
                <div :class="changeClass(item.statistics.normalizedWithPreferredCurrency.dayChange)">{{ formatPriceWithSign(item.statistics.normalizedWithPreferredCurrency.dayChange) }}</div>
            </template>
            <template v-slot:item.statistics.normalizedWithPreferredCurrency.totalProfitIncludingUnrealized="{ item }">
                <div :class="changeClass(item.statistics.normalizedWithPreferredCurrency.totalProfitIncludingUnrealized)">{{ formatPriceWithSign(item.statistics.normalizedWithPreferredCurrency.totalProfitIncludingUnrealized) }}</div>
            </template>
        </v-data-table>
    </div>
</template>

<script>
import UserDataHelperMixin from '../../infra/UserDataHelperMixin';
import PortfolioStockListMixin from '../../components/portfolio/PortfolioStockListMixin';
import CountryBadge from '../../components/controls/CountryBadge.vue';
import ValueAndChange from '../../components/controls/ValueAndChange.vue';
import StockPriceInfos from '../../components/stocks/StockPriceInfos.vue';
import PortfoliosOverviewSummary from './PortfoliosOverviewSummary.vue';
export default {
	components: { CountryBadge, ValueAndChange, StockPriceInfos, PortfoliosOverviewSummary },
    mixins: [UserDataHelperMixin, PortfolioStockListMixin],
    data() {
        return {
        }
    },
    computed: {
        headers() {
            return [
                { text: this.$t('portfoliosOverview.headers.symbol'), value: 'symbol' },
                { text: '', value: 'country', sortable: false },
                { text: this.$t('portfoliosOverview.headers.lastPrice'), value: 'stockDateTimeSnapshot.price', cellClass: 'colPrice', align: 'end' },
                { text: this.$t('portfoliosOverview.headers.dayChange'), value: 'stockDateTimeSnapshot.changePercent', cellClass: 'colSm', align: 'end' },
                { text: this.$t('portfoliosOverview.headers.low'), value: 'dayLow', align: 'end', sortable: false },
                { text: this.$t('portfoliosOverview.headers.high'), value: 'dayHigh', align: 'end', sortable: false },
                { text: this.$t('portfoliosOverview.headers.shares'), value: 'statistics.remainingStockShareCount', align: 'end' },
                { text: this.$t('portfoliosOverview.headers.avgCostPrice'), value: 'statistics.allTimeAverageShareBuyPrice', align: 'end' },
                { text: this.marketValueHeader, value: 'statistics.normalizedWithPreferredCurrency.totalMaterializedGrossValueIncludingUnrealized', align: 'end' },
                { text: this.dayGainUnrealizedHeader, value: 'statistics.normalizedWithPreferredCurrency.dayChange', align: 'end' },
                { text: this.totalGainUnrealizedHeader, value: 'statistics.normalizedWithPreferredCurrency.totalProfitIncludingUnrealized', align: 'end' }
            ]
        },
        marketValueHeader() {
            return `${this.$t('portfoliosOverview.headers.marketValue')} (${this.userPreferredCurrency})`
        },
        dayGainUnrealizedHeader() {
            return `${this.$t('portfoliosOverview.headers.dayGain')} (${this.userPreferredCurrency})`
        },
        totalGainUnrealizedHeader() {
            return `${this.$t('portfoliosOverview.headers.totalGain')} (${this.userPreferredCurrency})`
        }
    },
    methods: {
        buildChangeData(item) {
            return {
                val: item.stockDateTimeSnapshot.change,
                chg: item.stockDateTimeSnapshot.changePercent,
                chgSymbol: "%"
            }
        }
    }
}
</script>

<style lang="scss" scoped>
@import "../../assets/styles/breakpoints.scss";
.portfolioOverviewPage {
    width: 100%;
    padding: 20px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 25px;
    ::v-deep .colEmph {
        font-weight: bold;
    }
    ::v-deep .colPrice {
        .priceInfos {
            justify-content: flex-end;
            .currency{
                font-size: 0.6em;
            }
        }
    }
    ::v-deep .sub {
        font-size: 0.6em;
        color: var(--v-midGray-base);
    }
    ::v-deep .text-end.colSm {
        display: flex;
        justify-content: flex-end;
    }
    ::v-deep .colSm {
        font-size: 0.6em;
    }
    ::v-deep .positive {
        color: var(--v-greenUp-base);
    }
    ::v-deep .negative {
        color: var(--v-redDown-base);
    }
}
</style>